import { faSort } from '@fortawesome/pro-light-svg-icons';
import {
  faInfoCircle,
  faSortDown,
  faSortUp,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  CSSProperties,
  FC,
  ForwardedRef,
  ReactNode,
  RefObject,
} from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { getBaseActionClasses } from 'src/components/buttons-and-actions/button';
import { TableHeaderLabel } from 'src/components/data-display/table-header-label';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useHeaderHeight } from 'src/hooks/use-header-height';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';

// this is a workaround to stay compatible with the old styles.
// we should improve it once we remove the old styles.
// see https://prisma.atlassian.net/browse/UXX-866
function getColsWidths(widths: (string | number)[]) {
  const relativeWidths = widths.filter((col) => typeof col === 'number');
  const relativeWidthsSum = relativeWidths.reduce((a, b) => a + b, 0);

  const vars: Record<string, string> = {};
  widths.forEach((width, index) => {
    const value =
      typeof width === 'string'
        ? `calc(${width} + 2 * 0.8rem)`
        : `calc(100% * ${width / relativeWidthsSum})`;
    vars[`--col-w-${index + 1}`] = value;
  });

  // in styled-components these have been generated dynamically, but tailwind
  // needs static classes, so we "prebuild" them.
  // sadly the drawback is that the amount cannot be fully dynamic.
  // an upper limit of 30 columns per table should be ok for the moment.
  const classes = cn(
    widths.length >= 1 && `[&_td:nth-child(1),&_th:nth-child(1)]:w-(--col-w-1)`,
    widths.length >= 2 && `[&_td:nth-child(2),&_th:nth-child(2)]:w-(--col-w-2)`,
    widths.length >= 3 && `[&_td:nth-child(3),&_th:nth-child(3)]:w-(--col-w-3)`,
    widths.length >= 4 && `[&_td:nth-child(4),&_th:nth-child(4)]:w-(--col-w-4)`,
    widths.length >= 5 && `[&_td:nth-child(5),&_th:nth-child(5)]:w-(--col-w-5)`,
    widths.length >= 6 && `[&_td:nth-child(6),&_th:nth-child(6)]:w-(--col-w-6)`,
    widths.length >= 7 && `[&_td:nth-child(7),&_th:nth-child(7)]:w-(--col-w-7)`,
    widths.length >= 8 && `[&_td:nth-child(8),&_th:nth-child(8)]:w-(--col-w-8)`,
    widths.length >= 9 && `[&_td:nth-child(9),&_th:nth-child(9)]:w-(--col-w-9)`,
    widths.length >= 10 &&
      `[&_td:nth-child(10),&_th:nth-child(10)]:w-(--col-w-10)`,
    widths.length >= 11 &&
      `[&_td:nth-child(11),&_th:nth-child(11)]:w-(--col-w-11)`,
    widths.length >= 12 &&
      `[&_td:nth-child(12),&_th:nth-child(12)]:w-(--col-w-12)`,
    widths.length >= 13 &&
      `[&_td:nth-child(13),&_th:nth-child(13)]:w-(--col-w-13)`,
    widths.length >= 14 &&
      `[&_td:nth-child(14),&_th:nth-child(14)]:w-(--col-w-14)`,
    widths.length >= 15 &&
      `[&_td:nth-child(15),&_th:nth-child(15)]:w-(--col-w-15)`,
    widths.length >= 16 &&
      `[&_td:nth-child(16),&_th:nth-child(16)]:w-(--col-w-16)`,
    widths.length >= 17 &&
      `[&_td:nth-child(17),&_th:nth-child(17)]:w-(--col-w-17)`,
    widths.length >= 18 &&
      `[&_td:nth-child(18),&_th:nth-child(18)]:w-(--col-w-18)`,
    widths.length >= 19 &&
      `[&_td:nth-child(19),&_th:nth-child(19)]:w-(--col-w-19)`,
    widths.length >= 20 &&
      `[&_td:nth-child(20),&_th:nth-child(20)]:w-(--col-w-20)`,
    widths.length >= 21 &&
      `[&_td:nth-child(21),&_th:nth-child(21)]:w-(--col-w-21)`,
    widths.length >= 22 &&
      `[&_td:nth-child(22),&_th:nth-child(22)]:w-(--col-w-22)`,
    widths.length >= 23 &&
      `[&_td:nth-child(23),&_th:nth-child(23)]:w-(--col-w-23)`,
    widths.length >= 24 &&
      `[&_td:nth-child(24),&_th:nth-child(24)]:w-(--col-w-24)`,
    widths.length >= 25 &&
      `[&_td:nth-child(25),&_th:nth-child(25)]:w-(--col-w-25)`,
    widths.length >= 26 &&
      `[&_td:nth-child(26),&_th:nth-child(26)]:w-(--col-w-26)`,
    widths.length >= 27 &&
      `[&_td:nth-child(27),&_th:nth-child(27)]:w-(--col-w-27)`,
    widths.length >= 28 &&
      `[&_td:nth-child(28),&_th:nth-child(28)]:w-(--col-w-28)`,
    widths.length >= 29 &&
      `[&_td:nth-child(29),&_th:nth-child(29)]:w-(--col-w-29)`,
    widths.length >= 30 &&
      `[&_td:nth-child(30),&_th:nth-child(30)]:w-(--col-w-30)`
  );

  // in case a table has more than 30 columns (which can happen in edge cases for GDM where tables
  // have no upper limit), we don't want the page to crash, but we do not support correct sizing of columns
  // anymore. this is fine as the columns become so narrow anyway that good reading cannot be expected anymore.
  // for such a case using the table component in the first place might not be a good idea.
  if (widths.length > 30)
    console.warn(
      'Tables with more than 30 columns do not fully support correct sizing of columns.'
    );

  return { classes, vars };
}

export type SimpleTableProps = {
  respectHeader?: boolean;
  /**
   * Either specify a fixed width like `'5.5rem'` as a string or a relative width
   * like `1` as a number which would be treated just like `'1fr'` in a CSS grid.
   *
   * Let's say you have a table like this:
   * `tsx
   * <SimpleTable widths={[2, 1, '5.5rem']} />
   * `
   *
   * This means your last column has a width of 5.5rem (plus padding which will be added later)
   * and the other two columns get the remaining space will the first one will be 66.6% and the
   * second one 33.3%.
   */
  widths: (string | number)[];
  sticky?: boolean;
  children: ReactNode;
  light?: boolean;
  containsFormFields?: boolean;
  className?: string;
  hasPagination?: boolean;
};

/**
 * Don't use `<SimpleTable/>` directly. Prefer `<Table/>` which offers a better
 * API and wraps this component.
 * @deprecated
 */
export const SimpleTable: FC<SimpleTableProps> = ({
  respectHeader = true,
  children,
  light,
  containsFormFields = false,
  widths,
  sticky,
  className,
  hasPagination,
}) => {
  const header = useHeaderHeight();
  const colsWidths = getColsWidths(widths);
  const isNewDesignActive = useIsNewDesignActive();
  const defaultThTop = isNewDesignActive ? '0px' : '1px';
  return (
    <table
      ref={(element) => {
        if (!element) return;
        // this is a workaround to not leak the tbody through the rounded corners of the sticky header.
        // this might not be a perfect solution, but it's for a non-critical part of the UI (as
        // we don't have dynamic backgrounds it should be enough to do this once on mount).
        element.style.setProperty(
          '--rounded-mask-color',
          getNearestBgColor(element)
        );
      }}
      style={
        {
          '--th-top': respectHeader ? `${header.height}px` : defaultThTop,
          ...colsWidths.vars,
        } as CSSProperties
      }
      className={cn(
        `
          group/table relative w-full table-fixed border-separate
          border-spacing-0 bg-white
        `,
        light &&
          `
            [&_td]:border-b [&_td]:border-b-border-default
            [&_td,&_th]:border-l-0
            [&_td,&_th]:last:border-r-0
            [&_th]:bg-background-surface-minimal
          `,
        sticky &&
          // shadow needed as a fake border-top for scrolling
          '[&_th]:sticky [&_th]:top-(--th-top)',
        hasPagination &&
          `
            [&_tr:last-child_td:first-child]:rounded-bl-none
            [&_tr:last-child_td:last-child]:rounded-br-none
          `,
        colsWidths.classes,
        className
      )}
      {...{ 'data-contains-form-fields': containsFormFields }}
      {...(light ? { 'data-light': true } : {})}
    >
      {children}
    </table>
  );
};

function getNearestBgColor(element: HTMLElement | null) {
  let current = element?.parentElement;

  while (current) {
    const computedStyle = window.getComputedStyle(current);
    const bgColor = computedStyle.backgroundColor;

    if (bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
      return bgColor;
    }

    current = current.parentElement;
  }

  return 'var(--color-background-default)';
}

export const Thead: FC<ComponentPropsWithRef<'thead'>> = (props) => {
  return (
    <thead
      {...props}
      className={cn(
        // sticky needed for nested tables to properly overlay each other
        'sticky z-1',
        props.onClick && 'hover:cursor-pointer',
        props.className
      )}
    />
  );
};

export type TrProps = {
  isEven?: boolean;
  hideBorder?: boolean;
  isFocused?: boolean;
  light?: boolean;
  isSelected?: boolean;
} & ComponentPropsWithRef<'tr'>;

export const Tr: FC<TrProps> = ({
  isEven,
  hideBorder: _hideBorder,
  isFocused,
  light,
  isSelected,
  ref,
  ...props
}) => {
  return (
    <tr
      ref={ref}
      {...props}
      className={cn(
        'group/tr',
        isSelected &&
          'outline-2 outline-offset-[-0.2rem] outline-border-surface-selected',
        props.onClick && 'hover:cursor-pointer',
        !isSelected &&
          !isFocused &&
          !light &&
          (isEven === undefined
            ? 'even:bg-background-surface-minimal'
            : isEven
              ? 'bg-background-surface-minimal'
              : ''),

        props.className
      )}
    />
  );
};

type Align = 'left' | 'center' | 'right';

const baseCellStyle = cn('px-md py-lg');

export const thStyle = cn(
  baseCellStyle,
  'bg-background-surface-subdued',
  `
    border-t border-l border-t-white border-l-white px-0
    group-first/tr:border-t-border-default
    first:border-l-border-default
    group-first/tr:first:rounded-tl-xs
    group-data-light/table:group-first/tr:first:rounded-tl-none
    last:border-r last:border-r-border-default
    group-first/tr:last:rounded-tr-xs
    group-data-light/table:group-first/tr:last:rounded-tr-none
  `,

  // this is a workaround to not leak the tbody through the rounded corners of the sticky header.
  // we basically create two pseudo-elements that cover the rounded top corners
  // (by using a clip-path and the nearest background color of a parent element).
  `
    group-first/tr:first:before:absolute group-first/tr:first:before:-top-px
    group-first/tr:first:before:-left-px
    group-first/tr:first:before:size-(--radius-xs)
    group-first/tr:first:before:bg-(--rounded-mask-color)
    group-first/tr:first:before:[clip-path:path("M0_0_L4_0_A4_4_0_0_0_0_4_Z")]
    group-data-light/table:group-first/tr:first:before:hidden
    group-first/tr:last:before:absolute group-first/tr:last:before:-top-px
    group-first/tr:last:before:-right-px
    group-first/tr:last:before:size-(--radius-xs)
    group-first/tr:last:before:rotate-90
    group-first/tr:last:before:bg-(--rounded-mask-color)
    group-first/tr:last:before:[clip-path:path("M0_0_L4_0_A4_4_0_0_0_0_4_Z")]
    group-data-light/table:group-first/tr:last:before:hidden
  `
);

type ThProps = {
  align?: Align;
  /**
   * Rendered as a small caption above the header label. Usage is highly discouraged,
   * unless there is a very specific need for it.
   */
  colGroup?: ReactNode;
} & ComponentPropsWithRef<'th'>;

export const Th: FC<ThProps> = ({
  align = 'left',
  colGroup,
  children,
  ...props
}) => {
  return (
    <th
      {...props}
      className={cn(
        thStyle,
        'group/th',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        props.className
      )}
    >
      <TableHeaderLabel colGroup={colGroup}>{children}</TableHeaderLabel>
    </th>
  );
};

const getTdStyle = ({
  mode,
}: {
  mode?: 'highlight' | 'expand-row' | 'default';
}) =>
  cn(
    baseCellStyle,
    `
      border-l border-l-border-default
      group-last/tr:border-b group-last/tr:border-b-border-default
      first:border-l-border-default
      group-last/tr:first:rounded-bl-xs
      last:border-r last:border-r-border-default
      group-last/tr:last:rounded-br-xs
    `,
    'in-data-[contains-form-fields=true]:align-top',
    mode === 'highlight' &&
      `
        border-border-support-highlight-minimal
        bg-background-support-highlight-minimal
      `,
    mode === 'expand-row' &&
      'border-t border-t-white bg-background-surface-subdued'
  );

type TdProps = (
  | {
      align?: Align;
      mode?: 'default' | 'highlight';
    }
  | {
      /**
       * For 'mode="expand-row"', the `align` is always set to `'center'`.
       */
      align?: undefined;
      mode: 'expand-row';
    }
) &
  ComponentPropsWithRef<'td'>;

export const Td: FC<TdProps> = ({
  mode,
  align: passedAlign = 'left',

  ...props
}) => {
  const align = mode === 'expand-row' ? 'center' : passedAlign;
  return (
    <td
      {...props}
      className={cn(
        getTdStyle({ mode }),
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        props.className
      )}
    />
  );
};

export type SortableParams = { sortColumn: string; sortAscending: boolean };

type SortableParamsTuple = [
  SortableParams,
  (params: Partial<SortableParams>) => void,
];

type SortDirection = 'asc' | 'desc';

type SortableHeaderProps = {
  label: ReactNode;
  active: boolean;
  direction: SortDirection;
  onSort: (direction: SortDirection) => void;
  button?: (props: {
    buttonProps: ComponentPropsWithoutRef<'button'>;
    labelRef: RefObject<HTMLElement | null>;
  }) => ReactNode;
  info?: ReactNode;
  ref?: ForwardedRef<HTMLDivElement>;
} & ComponentPropsWithoutRef<'button'>;

export const ThButton: FC<ComponentPropsWithoutRef<'button'>> = (props) => {
  return (
    <button
      {...props}
      type="button"
      className={cn(
        getBaseActionClasses({}),
        `
          group/th-button w-full border-0 bg-transparent px-0 py-lg text-left
          [font-weight:inherit] text-inherit
        `,
        props.className
      )}
    />
  );
};

const labelStyle = cn(getBaseActionClasses({}), 'min-w-0 cursor-[inherit]');

const iconStyle = cn('text-label-sm');

export const SortableHeader: FC<SortableHeaderProps> = (props) => {
  const { label, active, direction, onSort, button, info, ref, ...rest } =
    props;

  const handleSort = useCallback(() => {
    if (direction === 'asc') {
      onSort('desc');
    } else {
      onSort('asc');
    }
  }, [direction, onSort]);

  const sortIcon = useMemo(() => {
    if (!active) return faSort;
    return direction === 'asc' ? faSortUp : faSortDown;
  }, [direction, active]);

  const ariaLabel = useMemo(() => {
    if (!active) {
      return 'Click to sort this column';
    }

    if (direction === 'desc') {
      return 'Click to enable ascending sorting';
    }

    return 'Click to enable descending sorting';
  }, [direction, active]);

  const labelRef = useRef<HTMLElement>(null);

  const buttonProps: ComponentPropsWithoutRef<'button'> = {
    onClick: (e) => {
      rest.onClick?.(e);
      handleSort();
    },
    className: cn('select-none', rest.className),
    ...rest,
    'aria-label': ariaLabel,
    children: (
      <div
        className={`
          inline-grid max-w-full grid-flow-col grid-cols-[minmax(0,1fr)]
          items-center gap-xs pr-md
        `}
        ref={ref}
      >
        <span className={labelStyle}>
          <TableHeaderLabel ref={labelRef}>{label}</TableHeaderLabel>
        </span>
        {!!info && (
          <Tooltip content={info}>
            {(targetProps) => (
              <FontAwesomeIcon icon={faInfoCircle} {...targetProps} />
            )}
          </Tooltip>
        )}
        <span aria-hidden className={iconStyle}>
          <FontAwesomeIcon icon={sortIcon} />
        </span>
      </div>
    ),
  };

  return (
    <th className={cn(thStyle, 'group/th p-0', props.className)}>
      {button ? (
        button({ buttonProps, labelRef })
      ) : (
        <ThButton {...buttonProps} />
      )}
    </th>
  );
};

type BasicParams<SortBy extends string> = {
  sortBy: SortBy;
  sortDirection: SortDirection;
};

type BasicPageParams<
  SortBy extends string,
  Params extends BasicParams<SortBy>,
> = {
  set: (value: Partial<Params>) => void;
  value: Params;
};

type DoubleSortableHeaderProps<
  SortBy extends string,
  Params extends BasicParams<SortBy>,
> = {
  labels: [string, string];
  columns: [Params['sortBy'], Params['sortBy']];
  params: BasicPageParams<SortBy, Params>;
  initialSortAscending?: boolean;
  button?: (props: {
    buttonProps: ComponentPropsWithoutRef<'button'>;
    labelRef: RefObject<HTMLElement | null>;
  }) => ReactNode;
} & ComponentPropsWithoutRef<'button'>;

export function DoubleSortableHeader<
  SortBy extends string,
  Params extends BasicParams<SortBy>,
>(props: DoubleSortableHeaderProps<SortBy, Params>) {
  const {
    labels,
    columns,
    params: { set: setParams },
    initialSortAscending = false,
    button,
    ...rest
  } = props;

  let sortColumn = props.params.value.sortBy;
  let sortDirection = props.params.value.sortDirection;
  let active = columns.includes(sortColumn);

  let label = labels[columns.indexOf(sortColumn)]
    ? labels[columns.indexOf(sortColumn)]
    : labels[0];

  const handleSort = useCallback(() => {
    const onSort = (sortDirection: SortDirection, sortBy: SortBy) => {
      if (!active) {
        // If the sorted columns switch, do not toggle the `sortAscending` state, use `initialSortAscending`
        setParams({
          sortBy,
          sortDirection: initialSortAscending ? 'asc' : 'desc',
        } as Partial<Params>);
      } else {
        setParams({ sortBy, sortDirection } as Partial<Params>);
      }
    };

    let currentSortBy = sortColumn;
    let sortAscending = sortDirection === 'asc';

    if (sortAscending && currentSortBy === columns[0]) {
      onSort('desc', columns[0]);
    } else if (!sortAscending && currentSortBy === columns[0]) {
      onSort('asc', columns[1]);
    } else if (sortAscending && currentSortBy === columns[1]) {
      onSort('desc', columns[1]);
    } else if (!sortAscending && currentSortBy === columns[1]) {
      onSort('asc', columns[0]);
    } else {
      onSort('asc', columns[0]);
    }
  }, [
    sortColumn,
    sortDirection,
    setParams,
    active,
    columns,
    initialSortAscending,
  ]);

  const sortIcon = useMemo(() => {
    if (!active) return faSort;
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  }, [sortDirection, active]);

  const ariaLabel = useMemo(() => {
    if (!active) {
      return 'Click to sort this column';
    }

    if (sortDirection === 'desc') {
      return 'Click to enable ascending sorting';
    }

    return 'Click to enable descending sorting';
  }, [sortDirection, active]);

  const labelRef = useRef<HTMLElement>(null);

  const buttonProps: ComponentPropsWithoutRef<'button'> = {
    onClick: (e) => {
      rest.onClick?.(e);
      handleSort();
    },
    ...rest,
    'aria-label': ariaLabel,
    children: (
      <div
        className={`
          inline-grid max-w-full grid-flow-col grid-cols-[minmax(0,1fr)]
          items-center gap-xs pr-md
        `}
      >
        <span className={labelStyle}>
          <TableHeaderLabel ref={labelRef}>{label}</TableHeaderLabel>
        </span>

        <span aria-hidden className={iconStyle}>
          <FontAwesomeIcon icon={sortIcon} />
        </span>
      </div>
    ),
  };

  return (
    <th className={cn(thStyle, 'group/th p-0', props.className)}>
      {button ? (
        button({ buttonProps, labelRef })
      ) : (
        <ThButton {...buttonProps} />
      )}
    </th>
  );
}

type GenericSortableHeaderProps = {
  label: ReactNode;
  sortableParams: SortableParamsTuple;
  column: string;
  initialSortAscending?: boolean;
  ref?: ForwardedRef<HTMLDivElement>;
};

export const GenericSortableHeader: FC<GenericSortableHeaderProps> = (
  props
) => {
  const {
    sortableParams: [{ sortColumn, sortAscending }, setParams],
    column,
    initialSortAscending = false,
    ref,
    ...rest
  } = props;

  const active = sortColumn === column;
  const direction = (active ? sortAscending : initialSortAscending)
    ? 'asc'
    : 'desc';

  return (
    <SortableHeader
      direction={direction}
      active={active}
      ref={ref}
      data-testid={`sort-${column}`}
      onSort={() => {
        if (!active) {
          // If the sorted columns switch, do not toggle the `sortAscending` state, use `initialSortAscending`
          setParams({
            sortColumn: column,
            sortAscending: initialSortAscending,
          });
        } else if (sortAscending) {
          setParams({ sortColumn: column, sortAscending: false });
        } else {
          setParams({ sortColumn: column, sortAscending: true });
        }
      }}
      {...rest}
    />
  );
};

type SpecificSortHeaderProps<
  Params extends { [key: string]: unknown },
  SortParam extends keyof Params,
  DirectionParam extends keyof Params,
> = {
  label: ReactNode;
  params: {
    value: Params & Record<DirectionParam, SortDirection>;
    set: (value: Partial<Params>) => void;
  };
  sortParam: SortParam;
  directionParam: DirectionParam;
  column: Params[SortParam];
  initialSortAscending?: boolean;
  button?: (props: {
    buttonProps: ComponentPropsWithoutRef<'button'>;
    labelRef: RefObject<HTMLElement | null>;
  }) => ReactNode;
  info?: ReactNode;
};

export function SpecificSortHeader<
  Params extends { [key: string]: unknown },
  SortParam extends keyof Params,
  DirectionParam extends keyof Params,
>(props: SpecificSortHeaderProps<Params, SortParam, DirectionParam>) {
  const {
    params,
    sortParam,
    directionParam,
    column,
    initialSortAscending = false,
    ...rest
  } = props;

  return (
    <SortableHeader
      direction={params.value[directionParam]}
      active={params.value[sortParam] === column}
      data-testid={`sort-${String(column)}`}
      onSort={(direction) => {
        if (params.value[sortParam] !== column) {
          // If the sorted columns switch, do not toggle the sorting order
          params.set({
            [sortParam]: column,
            [directionParam]: initialSortAscending ? 'asc' : 'desc',
          } as Partial<Params>);
        } else {
          params.set({
            [sortParam]: column,
            [directionParam]: direction,
          } as Partial<Params>);
        }
      }}
      {...rest}
    />
  );
}
